import { useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { ListItemNode, ListNode } from '@lexical/list';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkPlugin, createLinkMatcherWithRegExp } from '@lexical/react/LexicalAutoLinkPlugin';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { $generateNodesFromDOM } from '@lexical/html';
import { TRANSFORMERS } from '@lexical/markdown';
import { $createParagraphNode, $getRoot, type LexicalEditor } from 'lexical';
import classNames from 'classnames/bind';
import { optionalProp } from '../core';
import { NextgenDOMPurify } from '../utils/NextgenDOMPurify/NextgenDOMPurify';
import { useConfig } from '../Config/ConfigProvider';
import { NextgenImage } from '../NextgenImage/NextgenImage';
import { ListMaxIndentLevelPlugin, CustomOnChangePlugin, DragDropPastePlugin } from './plugins';
import Toolbar from './Toolbar';
import { rteTheme } from './subComponent/helper';
import styles from './RichTextEditor.module.scss';
import { type RichTextEditorPropTypes } from './types/propsType';
import { validateURL } from './subComponent/URLValidation';
import { AdditionBtnStyle } from './types/enums';

function onError() {
  return;
}

const URL_REGEX =
  /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

const EMAIL_REGEX =
  /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;

const MATCHERS = [
  createLinkMatcherWithRegExp(URL_REGEX, (text) => (text.startsWith('http') ? text : `https://${text}`)),
  createLinkMatcherWithRegExp(EMAIL_REGEX, (text) => `mailto:${text}`),
];

export function getInitialEditorState(editor: LexicalEditor, newState: string) {
  editor.update(() => {
    const parser = new DOMParser();
    const sanitizeInitialState = NextgenDOMPurify(newState || '');
    const Nodes = $generateNodesFromDOM(editor, parser.parseFromString(sanitizeInitialState, 'text/html'));
    const root = $getRoot();
    root.clear();
    const paragraphNode = $createParagraphNode();
    Nodes.forEach((node) => paragraphNode.append(node));
    root.append(paragraphNode);
  });
}
const cx = classNames.bind(styles);

function RichTextEditor(props: Readonly<RichTextEditorPropTypes>) {
  const {
    initialEditorState = '',
    getHTMLUpdate,
    disabledFeatureList = [],
    onImageUpload,
    uploadImageURL,
    loaderImg,
    className = '',
  } = props;
  const hideTable = disabledFeatureList.includes(AdditionBtnStyle.Table);
  // hideEmoji = disabledFeatureList.includes(AdditionBtnStyle.Emoji);
  const [isLoading, setIsLoading] = useState(false);
  const configs = useConfig();
  const CClassname = cx('editor-container', { 'rte-container-loading': isLoading }, className);
  const loaderImgUrl = loaderImg ? loaderImg : `${configs.NEXT_ASSETS_BASE_PATH}/images/Loader.gif`;

  const initialConfig = {
    namespace: 'Nextgen-RTE',
    theme: rteTheme,
    editorState: (state: LexicalEditor) => getInitialEditorState(state, initialEditorState),
    onError,
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      LinkNode,
      AutoLinkNode,
      ...(hideTable ? [] : [TableNode, TableCellNode, TableRowNode]),
      // ...(hideEmoji ? [] : [EmojiNode]),
    ],
  };
  return (
    <section className={CClassname}>
      {isLoading && (
        <div className={styles['rte-loading']}>
          <div>
            <NextgenImage src={loaderImgUrl} alt="&#9728;" />
          </div>
        </div>
      )}
      <LexicalComposer initialConfig={{ ...initialConfig, editable: !isLoading }}>
        <Toolbar
          disabledFeatureList={disabledFeatureList}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          {...optionalProp('getHTMLUpdate', getHTMLUpdate)}
          {...optionalProp('onImageUpload', onImageUpload)}
          {...optionalProp('uploadImageURL', uploadImageURL)}
        />
        <RichTextPlugin
          contentEditable={<ContentEditable className={styles['editor-box']} data-testid="rte-editable-testid" />}
          placeholder={<span />}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <CustomOnChangePlugin {...optionalProp('getHTMLUpdate', getHTMLUpdate)} />
        <HistoryPlugin />
        {/* {hideEmoji ? (
          <></>
        ) : (
          <>
            <EmojiPickerPlugin />
            <EmojiTypeaheadPlugin />
          </>
        )} */}
        <AutoFocusPlugin />
        <ListMaxIndentLevelPlugin maxDepth={7} />
        <TabIndentationPlugin />
        {/* Removed Image Plugin dependent on ImageComponent.tsx */}
        <ListPlugin />
        <LinkPlugin validateUrl={validateURL} />
        <AutoLinkPlugin matchers={MATCHERS} />
        <DragDropPastePlugin />
        {hideTable ? <></> : <TablePlugin />}

        <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
      </LexicalComposer>
    </section>
  );
}
RichTextEditor.displayName = 'RichTextEditor';
export default RichTextEditor;
